/* global window */

import get from 'lodash/get';
import isMobileJS from 'ismobilejs';

import Embed from './assets/Embed';
import { isBrowserEdge } from './utils';

/**
 * A video embedded asset.
 */
export default class VideoEmbed extends Embed {
  /**
   * Create a video embed asset.
   * @param {string} id - Embed ID.
   * @param {Object} settings - Settings object.
   * @param {Object} defaultAttributes - optional attributes.
   */
  constructor(id, settings, defaultAttributes) {
    super(id, settings, defaultAttributes);
    this.playerIsSticky = false;
    this.playerSkipExitEvent = false;
    this.playerSkipEnterEvent = false;
    this.playerAdTagUrl = null;
    this.playerIndex = 0;
    this.adStartEvent = 'inViewPlay';
  }

  /**
   * @return {boolean} Whether ads are enabled.
   */
  get adsEnabled() {
    return !get(this.attributes, 'ads_disabled', false);
  }

  /**
   * @return {boolean} Whether embed contains captions.
   */
  get hasCaptions() {
    const captions = get(this.attributes, 'media.captions', []);
    return captions.lentgh > 0;
  }

  /**
   * @return {boolean} Whether player can be sticky.
   */
  get canBeSticky() {
    return true;
  }

  /**
   * @return {Object} Text Track object with captions data.
   */
  get textTrack() {
    return {
      kind: 'captions',
      mode: 'disabled',
      src: get(this.attributes, 'media.captions.0.url'),
      language: get(this.attributes, 'media.captions.0.language'),
      srclang: get(this.attributes, 'media.captions.0.language'),
      label: get(this.attributes, 'media.captions.0.full_language'),
      default: false,
    };
  }

  /**
   * Returns source Ad Tag URL.
   * @return {string} Ad Tag URL
   */
  get adTagUrl() {
    return this.playerAdTagUrl;
  }

  /**
   * Sets source Ad Tag URL.
   * @param {string} adTagUrl Ad Tag URL
   */
  setAdTagUrl(adTagUrl) {
    this.playerAdTagUrl = adTagUrl;
  }

  /**
   * Returns source sticky state.
   * @returns {boolean} playerIsSticky
   */
  get isSticky() {
    return this.playerIsSticky;
  }

  /**
   * Sets source sticky state.
   * @param {boolean} isSticky
   */
  setIsSticky(isSticky) {
    this.playerIsSticky = isSticky;
  }

  /**
  * Returns source skip next inview exit event.
  * @returns {boolean} skipNextExit
  */
  get skipExitEvent() {
    return this.playerSkipExitEvent;
  }

  /**
  * Sets source skip exit event state.
  * @param {boolean} skipExit
  */
  setSkipExitEvent(skipExitEvent) {
    this.playerSkipExitEvent = skipExitEvent;
  }
  /**
  * Returns source skip next inview enter event.
  * @returns {boolean} skipNextEnter
  */
  get skipEnterEvent() {
    return this.playerSkipEnterEvent;
  }
  /**
  * Sets source skip enter event state.
  * @param {boolean} skipEnterEvent
  */
  setSkipEnterEvent(skipEnterEvent) {
    this.playerSkipEnterEvent = skipEnterEvent;
  }

  /**
   * Sets source Ad start event.
   * @param {string} adStartEvent Ad start event
   */
  setAdStartEvent(adStartEvent) {
    this.adStartEvent = adStartEvent;
  }

  /**
   * Whether this source type requires recommendations.
   * @return {boolean}
   */
  get requiresRecommendations() {
    return true;
  }

  /**
   * Returns source playlist index.
   * @return {integer} playlist index
   */
  get index() {
    return this.playerIndex;
  }

  /**
   * Sets source playlist index.
   * @param {integer} index playlist index
   */
  setIndex(index) {
    this.playerIndex = index;
  }

  /**
   * @return {integer} stream type.
   */
  get type() {
    return get(this.attributes, 'media.type', 1);
  }

  /**
   * @return {boolean} is live stream.
   */
  get isLiveStream() {
    return this.type === 2 || this.type === 888;
  }

  /**
   * @return {boolean} has anime.
   */
  get hasAnime() {
    return !this.isLiveStream;
  }

  /**
   * @return {Object} tech override settings.
   */
  get techSettings() {
    const requiresNativeOverride = isMobileJS.android.device || isBrowserEdge(window.navigator);

    if (!requiresNativeOverride) {
      return {};
    }

    return {
      html5: {
        hls: {
          overrideNative: true,
        },
        nativeVideoTracks: false,
        nativeAudioTracks: false,
        nativeTextTracks: false,
      },
    };
  }

  /**
   * @return {string} Amazon bid slot ID.
   */
  get slotId() {
    return 'videoSlot';
  }

  /**
   * @return {boolean} use Ad Tag override.
   */
  get hasAdTagOverride() {
    return false;
  }
}
